import React from "react";
import moment from "moment";
import {
	Alert,
	Box,
	CircularProgress,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { currencyFormatter } from "utilities/currencyFormatter";

const formatDate = (value) => (value ? moment(value).format("LL") : "—");
const formatMonth = (value) => (value ? value.replace("-", " ") : "—");

// Body of the void dialog: why a payment can't be voided, or what changes if it is.
export default function VoidPaymentPreview({ payment, preview, isLoading, error }) {
	if (isLoading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
				<CircularProgress size={28} />
			</Box>
		);
	}
	if (error) {
		return <Alert severity="error">{error}</Alert>;
	}
	if (!payment || !preview) return null;

	const heading = `Void payment of ${currencyFormatter(parseFloat(payment.amount) || 0)} dated ${formatDate(
		payment.date_paid || payment.date_created
	)}?`;

	if (!preview.can_void) {
		return (
			<Box>
				<Typography sx={{ mb: 1.5 }}>{heading}</Typography>
				<Alert severity="warning">
					This payment can't be voided from here:
					<ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
						{(preview.reasons || []).map((reason) => (
							<li key={reason}>{reason}</li>
						))}
					</ul>
				</Alert>
			</Box>
		);
	}

	if (preview.mode === "latest") {
		return <Typography>{heading} This is the latest payment, so nothing else changes.</Typography>;
	}

	const later = preview.later_payments || [];
	return (
		<Box>
			<Typography sx={{ mb: 1.5 }}>{heading}</Typography>
			<Typography sx={{ mb: 1 }}>
				Paid-up-to date moves from <strong>{formatDate(preview.period_end_before)}</strong> to{" "}
				<strong>{formatDate(preview.period_end_after)}</strong> ({preview.months_covered_before} →{" "}
				{preview.months_covered_after} months covered).
			</Typography>
			{parseFloat(preview.unallocated_after) > 0 && (
				<Typography sx={{ mb: 1 }}>
					Part payment carried forward: {currencyFormatter(parseFloat(preview.unallocated_after))}.
				</Typography>
			)}
			{later.length > 0 && (
				<>
					<Typography sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
						Later payments are relabelled (amounts unchanged):
					</Typography>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Date paid</TableCell>
								<TableCell align="right">Amount</TableCell>
								<TableCell>Month paid for</TableCell>
								<TableCell>Next due</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{later.map((row) => (
								<TableRow key={row.payment_id}>
									<TableCell>{formatDate(row.date_paid)}</TableCell>
									<TableCell align="right">{currencyFormatter(parseFloat(row.amount) || 0)}</TableCell>
									<TableCell>
										{formatMonth(row.month_before)} → {formatMonth(row.month_after)}
									</TableCell>
									<TableCell>
										{formatDate(row.next_due_before)} → {formatDate(row.next_due_after)}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</>
			)}
		</Box>
	);
}
